import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, Sparkles, HandHeart, BookOpenCheck, Compass, Leaf, ShieldCheck, Mail, Phone, MapPin, Clock, ArrowRight, Lock } from "lucide-react";
import API, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentProvider";
import { Reveal } from "@/components/Reveal";

const VALUE_ICONS = [ShieldCheck, HandHeart, BookOpenCheck, Sparkles, Compass, Leaf, ShieldCheck];

const CardBlock = ({ b }) => (
  <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
    {b.title && <Reveal><h2 className="mb-10 text-center font-serif-d text-4xl font-semibold text-[#4b3a2f]">{b.title}</h2></Reveal>}
    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
      {(b.items || []).map((c, i) => (
        <Reveal key={i} delay={i * 0.06}>
          <div className="h-full overflow-hidden rounded-2xl border border-[#e8ded4] bg-white">
            {c.image_url && <img src={c.image_url} alt={c.title} className="aspect-[4/3] w-full object-cover" />}
            <div className="p-6">
              {c.title && <h3 className="font-serif-d text-2xl font-semibold text-[#4b3a2f]">{c.title}</h3>}
              {c.text && <p className="mt-2 text-sm leading-relaxed text-[#6b5b52]">{c.text}</p>}
              {c.link && <Link to={c.link} className="gold-underline mt-4 inline-block text-sm font-semibold text-[#a47b52]">Ver más</Link>}
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const FaqBlock = ({ b }) => (
  <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
    <div className="space-y-3">
      {(b.items || []).map((f, i) => (
        <Reveal key={i} delay={i * 0.05}>
          <details className="group rounded-2xl border border-[#e8ded4] bg-white p-6" data-testid={`faq-item-${i}`}>
            <summary className="flex cursor-pointer list-none items-center justify-between font-serif-d text-xl font-semibold text-[#4b3a2f]">
              {f.q}
              <ChevronDown size={18} className="text-[#c9a24b] transition-transform group-open:rotate-180" />
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-[#6b5b52]">{f.a}</p>
          </details>
        </Reveal>
      ))}
    </div>
  </section>
);

const ContactBlock = ({ b }) => {
  const { t } = useContent();
  const hours = (t("contact.hours", "") || "").split("\n").filter(Boolean);
  return (
    <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
      <div className="rounded-3xl bg-[#4b3a2f] p-10 text-[#f2e9dd]">
        {b.title && <h2 className="font-serif-d text-4xl font-semibold">{b.title}</h2>}
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {t("contact.email", "") && (
            <p className="flex items-center gap-3 text-sm"><Mail size={17} className="text-[#c9a24b]" /> {t("contact.email")}</p>
          )}
          {t("contact.whatsapp", "") && (
            <p className="flex items-center gap-3 text-sm"><Phone size={17} className="text-[#c9a24b]" /> {t("contact.whatsapp")}</p>
          )}
          {t("contact.address", "") && (
            <p className="flex items-center gap-3 text-sm"><MapPin size={17} className="text-[#c9a24b]" /> {t("contact.address")}</p>
          )}
          {hours.length > 0 && (
            <p className="flex items-start gap-3 text-sm"><Clock size={17} className="mt-0.5 shrink-0 text-[#c9a24b]" /><span>{hours.map((h, i) => <span key={i} className="block">{h}</span>)}</span></p>
          )}
        </div>
      </div>
    </section>
  );
};

const BLOCKS = {
  text: ({ b }) => (
    <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
      <div className={`grid items-center gap-10 ${b.image_url ? "lg:grid-cols-2" : ""}`}>
        <Reveal>
          {b.title && <h2 className="font-serif-d text-4xl font-semibold leading-tight text-[#4b3a2f]">{b.title}</h2>}
          {b.subtitle && <p className="mt-3 font-serif-d text-2xl italic text-[#a47b52]">{b.subtitle}</p>}
          {(b.body || "").split("\n").filter(Boolean).map((p, i) => (
            <p key={i} className="mt-4 leading-relaxed text-[#4b3a2f]/90">{p}</p>
          ))}
          {b.button_text && (
            <Link to={b.button_link || "/"} className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#4b3a2f] px-7 py-3.5 text-sm font-semibold text-[#f2e9dd] transition-colors hover:bg-[#c9a24b] hover:text-[#4b3a2f]">
              {b.button_text} <ArrowRight size={15} />
            </Link>
          )}
        </Reveal>
        {b.image_url && (
          <Reveal delay={0.1}>
            <div className="arch-frame overflow-hidden"><img src={b.image_url} alt={b.title || ""} className="aspect-[4/5] w-full object-cover" /></div>
          </Reveal>
        )}
      </div>
    </section>
  ),
  cards: CardBlock,
  banner: ({ b }) => (
    <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <Reveal>
        <div className="flex flex-wrap items-center justify-between gap-5 rounded-3xl bg-[#4b3a2f] p-8 text-[#f2e9dd]">
          <p className="font-serif-d text-2xl">{b.text}</p>
          {b.button_text && (
            <Link to={b.button_link || "/"} className="rounded-full bg-[#c9a24b] px-6 py-3 text-sm font-semibold text-[#4b3a2f] hover:bg-[#b8913b]">
              {b.button_text}
            </Link>
          )}
        </div>
      </Reveal>
    </section>
  ),
  faq: FaqBlock,
  video: ({ b }) => (
    <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
      {b.title && <Reveal><h2 className="mb-8 text-center font-serif-d text-4xl font-semibold text-[#4b3a2f]">{b.title}</h2></Reveal>}
      <Reveal><div className="aspect-video overflow-hidden rounded-2xl">
        <iframe src={b.video_url} title={b.title || "Video"} className="h-full w-full" allowFullScreen />
      </div></Reveal>
    </section>
  ),
  contact: ContactBlock,
};

export const PageRenderer = ({ page }) => (
  <div className="min-h-screen bg-[#faf7f2] pb-24 pt-[120px]" data-testid="dynamic-page">
    <h1 className="grain bg-[#4b3a2f] py-14 text-center font-serif-d text-5xl font-semibold text-[#f2e9dd]">{page.title}</h1>
    {(page.blocks || []).map((b, i) => {
      const R = BLOCKS[b.type];
      return R ? <R key={i} b={b} /> : null;
    })}
  </div>
);

const DynamicPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(undefined); // undefined = cargando, null = sin acceso
  const [notFound, setNotFound] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
    setPage(undefined);
    API.get(`/pages/by-slug/${slug}`)
      .then((r) => setPage(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else setPage(null);
      });
  }, [slug, user]);

  if (notFound) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-[#faf7f2] pt-[72px] text-center">
      <p className="font-serif-d text-4xl text-[#4b3a2f]">Página no encontrada</p>
      <Link to="/" className="rounded-full bg-[#4b3a2f] px-8 py-3 text-sm text-[#f2e9dd]">Volver al inicio</Link>
    </div>
  );

  if (page === undefined) return <div className="flex min-h-screen items-center justify-center bg-[#faf7f2] pt-[72px]"><p className="animate-pulse text-[#6b5b52]">Cargando…</p></div>;

  if (page === null) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#faf7f2] px-4 pt-[72px] text-center" data-testid="page-gate">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#f2e9dd] text-[#a47b52]">
        <Lock size={24} />
      </span>
      <div>
        <h1 className="font-serif-d text-4xl font-semibold text-[#4b3a2f]">Acceso restringido</h1>
        <p className="mx-auto mt-3 max-w-md text-sm text-[#6b5b52]">Esta página es privada. Inicia sesión con tu cuenta para poder verla.</p>
      </div>
      <div className="flex flex-wrap justify-center gap-4">
        <Link to={`/iniciar-sesion?next=/p/${slug}`} data-testid="page-gate-login" className="rounded-full bg-[#4b3a2f] px-8 py-3.5 text-sm font-semibold text-[#f2e9dd]">Iniciar sesión</Link>
        <Link to="/registrarse" className="rounded-full border border-[#4b3a2f] px-8 py-3.5 text-sm font-semibold text-[#4b3a2f]">Crear cuenta</Link>
      </div>
    </div>
  );

  return <PageRenderer page={page} />;
};

export default DynamicPage;
